import React, { useCallback, useEffect, useState } from "react";
import { fetchReconciliationGaps, fetchReconciliationSummary } from "../api/client";
import SeverityBadge from "../components/SeverityBadge";
import type { ReconciliationGap, ReconciliationSummary } from "../types";
import { useDateFormat } from "../preferences/PreferencesProvider";
import { formatDateTime, parseInstant, utcDaysAgo, utcToday } from "../utils/datetime";
import { csvDocument, downloadCsv } from "../utils/csv";

// ── helpers ──────────────────────────────────────────────────────────────────

// UTC, like the per-day buckets this page counts. A window computed in the
// browser's zone began part-way through a UTC day, so the first bucket held a
// fraction of a day's events — and this page flags a day with too few events
// as a collector gap. The boundary was manufacturing the exact finding the
// page exists to report.
function defaultDateFrom(): string {
  return utcDaysAgo(7);
}

function defaultDateTo(): string {
  return utcToday();
}

/** Hours since an ISO timestamp. Returns null if timestamp is null/unparsable. */
function hoursSince(ts: string | null): number | null {
  const instant = parseInstant(ts);
  if (!instant) return null;
  return (Date.now() - instant.getTime()) / 3_600_000;
}

type HealthColour = "green" | "yellow" | "red" | "unknown";

function healthColour(ts: string | null): HealthColour {
  const h = hoursSince(ts);
  if (h === null) return "unknown";
  if (h < 1) return "green";
  if (h < 6) return "yellow";
  return "red";
}

const healthDot: Record<HealthColour, string> = {
  green: "bg-green-500",
  yellow: "bg-yellow-400",
  red: "bg-red-500",
  // Not grey. Inside this panel an absent timestamp means the API found no
  // event in its lookback window, or could not look — and on tables holding
  // six figures of rows neither of those is benign. A neutral dot next to the
  // words "No data" is how a source that has stopped entirely ends up being
  // the calmest thing on the page.
  unknown: "bg-red-500",
};

const healthLabel: Record<HealthColour, string> = {
  green: "Live (< 1 h)",
  yellow: "Delayed (1–6 h)",
  red: "Stale (> 6 h)",
  unknown: "No recent events",
};

function gapStatusLabel(status: ReconciliationGap["status"]): string {
  switch (status) {
    case "ual_gap":    return "UAL gap";
    case "pbi_gap":    return "PBI gap";
    case "both_gap":   return "Both gap";
    case "ok":         return "> 20% delta";
  }
}

function gapSeverityLevel(status: ReconciliationGap["status"]): "critical" | "medium" | "info" {
  if (status === "both_gap" || status === "ual_gap" || status === "pbi_gap") return "critical";
  return "medium";
}

// ── bar helper ────────────────────────────────────────────────────────────────

function Bar({ value, max, colour }: { value: number; max: number; colour: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-100 rounded h-3">
        <div
          className={`h-3 rounded ${colour}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-gray-600 w-12 text-right">{value.toLocaleString()}</span>
    </div>
  );
}

// ── CSV export ────────────────────────────────────────────────────────────────

function exportGapsCsv(gaps: ReconciliationGap[]): void {
  // These five fields cannot currently contain a comma, but the interpolation
  // that assumed so was one added column away from silently shifting every
  // field after it. Same writer as the eDiscovery export now.
  downloadCsv(
    "reconciliation-gaps.csv",
    csvDocument(
      ["Date", "UAL Count", "PBI Count", "Delta", "Status"],
      gaps.map((g) => [g.date, g.ualCount, g.pbiCount, g.delta, g.status])
    )
  );
}

// ── component ─────────────────────────────────────────────────────────────────

export default function ReconciliationPage() {
  const dateFormat = useDateFormat();
  const formatTs = (ts: string | null) => formatDateTime(ts, dateFormat);

  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo]     = useState(defaultDateTo);

  const [summary, setSummary]   = useState<ReconciliationSummary | null>(null);
  const [gaps, setGaps]         = useState<ReconciliationGap[]>([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, g] = await Promise.all([
        fetchReconciliationSummary({ date_from: dateFrom, date_to: dateTo }),
        fetchReconciliationGaps({ date_from: dateFrom, date_to: dateTo }),
      ]);
      setSummary(s);
      setGaps(g);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load reconciliation data");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  // Per-day table data (sorted ascending)
  const days = summary
    ? Object.keys(summary.ual.byDay).sort()
    : [];
  const maxDayCount = summary
    ? Math.max(
        ...days.map((d) =>
          Math.max(summary.ual.byDay[d] ?? 0, summary.pbi.byDay[d] ?? 0)
        ),
        1
      )
    : 1;

  // Collector health
  const ualColour = summary ? healthColour(summary.collectorHealth.ualLastEvent) : "unknown";
  const pbiColour = summary ? healthColour(summary.collectorHealth.pbiLastEvent) : "unknown";

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Source Reconciliation</h1>
          <p className="text-sm text-gray-500 mt-1">
            Cross-validate PBI Activity Events and O365 UAL — detect collector gaps and confirm backfill completeness.
          </p>
        </div>
        <button
          onClick={() => exportGapsCsv(gaps)}
          disabled={gaps.length === 0}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Export CSV
        </button>
      </div>

      {/* Date range picker */}
      <div className="flex flex-wrap items-end gap-4 bg-white rounded-xl border border-gray-200 p-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">From (UTC)</label>
          <input
            type="date"
            value={dateFrom}
            max={dateTo}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">To (UTC)</label>
          <input
            type="date"
            value={dateTo}
            min={dateFrom}
            onChange={(e) => setDateTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 disabled:opacity-60"
        >
          {loading ? "Loading…" : "Apply"}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
          {error}
        </div>
      )}

      {summary && (
        <>
          {/* Summary boxes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* UAL */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                UAL Events (O365)
              </p>
              <p className="text-4xl font-bold text-blue-700">
                {summary.ual.totalEvents.toLocaleString()}
              </p>
              <p className="text-sm text-gray-500 mt-1">{dateFrom} — {dateTo} UTC</p>
            </div>
            {/* PBI Activity */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                PBI Activity Events
              </p>
              <div className="flex items-end gap-3">
                <p className="text-4xl font-bold text-indigo-700">
                  {summary.pbi.totalEvents.toLocaleString()}
                </p>
                {summary.discrepancies.totalDelta > 0 && (
                  <span className="mb-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    &Delta; {summary.discrepancies.totalDelta.toLocaleString()}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1">{dateFrom} — {dateTo} UTC</p>
            </div>
          </div>

          {/* Collector Health */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-sm font-semibold text-gray-700 mb-4">Collector Health</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <span className={`mt-1 flex-shrink-0 w-3 h-3 rounded-full ${healthDot[ualColour]}`} />
                <div>
                  <p className="text-sm font-medium text-gray-900">UAL Collector</p>
                  <p className="text-xs text-gray-500">
                    Last event: {formatTs(summary.collectorHealth.ualLastEvent)}
                  </p>
                  <p className={`text-xs font-medium mt-0.5 ${ualColour === "green" ? "text-green-600" : ualColour === "yellow" ? "text-yellow-600" : "text-red-600"}`}>
                    {healthLabel[ualColour]}
                  </p>
                  {summary.collectorHealth.ualGapDays.length > 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      Gap days: {summary.collectorHealth.ualGapDays.join(", ")}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className={`mt-1 flex-shrink-0 w-3 h-3 rounded-full ${healthDot[pbiColour]}`} />
                <div>
                  <p className="text-sm font-medium text-gray-900">PBI Activity Collector</p>
                  <p className="text-xs text-gray-500">
                    Last event: {formatTs(summary.collectorHealth.pbiLastEvent)}
                  </p>
                  <p className={`text-xs font-medium mt-0.5 ${pbiColour === "green" ? "text-green-600" : pbiColour === "yellow" ? "text-yellow-600" : "text-red-600"}`}>
                    {healthLabel[pbiColour]}
                  </p>
                  {summary.collectorHealth.pbiGapDays.length > 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      Gap days: {summary.collectorHealth.pbiGapDays.join(", ")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Events per day — bar table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-700">Events per Day</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      UAL
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      PBI Activity
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-20">
                      Delta
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {days.map((d) => {
                    const ual = summary.ual.byDay[d] ?? 0;
                    const pbi = summary.pbi.byDay[d] ?? 0;
                    const delta = Math.abs(ual - pbi);
                    return (
                      <tr key={d} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-gray-700">{d}</td>
                        <td className="px-4 py-3 w-64">
                          <Bar value={ual} max={maxDayCount} colour="bg-blue-500" />
                        </td>
                        <td className="px-4 py-3 w-64">
                          <Bar value={pbi} max={maxDayCount} colour="bg-indigo-400" />
                        </td>
                        <td className="px-4 py-3 text-right">
                          {delta > 0 ? (
                            <span className="text-yellow-700 font-medium">{delta}</span>
                          ) : (
                            <span className="text-green-600">0</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Gaps */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-700">Gaps Detected</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Days where one source has 0 events or a &gt;20% delta vs the other.
              </p>
            </div>
            {gaps.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-green-600">
                <svg className="w-10 h-10 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="font-semibold">All sources reconciled</p>
                <p className="text-sm text-gray-400 mt-1">No significant gaps in the selected period.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">UAL Count</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">PBI Count</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Delta</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {gaps.map((g) => (
                      <tr key={g.date} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-gray-700">{g.date}</td>
                        <td className="px-4 py-3 text-right text-gray-700">{g.ualCount.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-gray-700">{g.pbiCount.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right font-medium text-yellow-700">{g.delta.toLocaleString()}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1.5">
                            <SeverityBadge level={gapSeverityLevel(g.status)} />
                            <span className="text-xs text-gray-600">{gapStatusLabel(g.status)}</span>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {!summary && !loading && !error && (
        <div className="text-center text-gray-400 py-16">Select a date range and click Apply.</div>
      )}
    </div>
  );
}
